import { Mutex } from 'async-mutex';
import { GraphQLClient } from 'graphql-request';
import { cloneDeep } from 'lodash';
import {
  DraftFile,
  FileEntityTypeEnum,
  FileLocation,
  FileLocationEnum,
  FileTagEnum,
  FileTags,
  PublicFile,
} from 'src/generated/graphql-types';
import store, { dispatch } from 'src/redux/config/configureStore';
import { FileTagsTypesEnum, FileTagsValues, Role, UserRolesEnum } from 'src/types.d';
import { getDraftFilesById } from 'src/utils/apiRequests/getDraftFilesById';
import { getInvestorDraftFilesById } from 'src/utils/apiRequests/getInvestorDraftFilesById';
import { getInvestorDraftProjectFundFiles } from 'src/utils/apiRequests/getInvestorDraftProjectFundFiles';
import { getInvestorPublicFiles } from 'src/utils/apiRequests/getInvestorPublisFiles';
import { getInvestorPublicProjectFundFiles } from 'src/utils/apiRequests/getInvestorPublishProjectFundFiles';
import {
  SyncedFiles,
  setLoadingUploadProgressBar,
  setSyncedFiles,
  setTotalToLoad,
  syncFilesWithServerEnum,
} from './files.slice';

export interface DraftFilesApiData {
  operatorId: string;
  projectId?: string;
  tags?: FileTags[];
  tagType?: FileTagsTypesEnum[];
}

// Define a mutex for synchronizing access to the store
const mutex = new Mutex();

//Holds the constants for the file group is for syncing files
export const ALL_DOCUMENT_CENTER_FILES = 'ALL_DOCUMENT_CENTER_FILES';
export const ALL_OFFERINGS_FILES = 'ALL_OFFERINGS_FILES';
export const ALL_ENTITIES_MAIN_IMAGES = 'ALL_ENTITIES_MAIN_IMAGES';

//Makes a given Tags collection unique
function makeUniqueTags(tags: FileTags[]): FileTags[] {
  const filtered = tags.filter(tag => tag); //filter null
  let set = [...new Set(filtered)]; //filter duplicates

  //create collection with string Items for deep compare
  //This is needed to remove duplicates such as [{ type: INVESTOR , value: 'a' } ,{ value: 'a', type: INVESTOR }]
  let uniqueStringItems: string[] = [];
  let uniqueItems: FileTags[] = [];

  //go over each set item
  set.forEach((item: FileTags) => {
    //create a unique key in str value
    const strItem = `${item.type} ${item.value} ${item.extra} ${item.hidden}`;

    //skip exiting items with different
    if (uniqueStringItems.includes(strItem)) return;

    //add items
    uniqueStringItems.push(strItem);
    uniqueItems.push(item);
  });

  return uniqueItems;
}

//Concat two tags collection
export function concatTags(existingTags: FileTags[], newTags: FileTags[]): FileTags[] {
  //if no existing tags found return the new tags
  if (!existingTags) return makeUniqueTags(newTags);

  //if no new tags found return the existing tags
  if (!newTags) return makeUniqueTags(existingTags);

  //else both has items - concat
  const combinedTags = existingTags.concat(newTags);

  //return the combined tags
  return makeUniqueTags(combinedTags);
}

/**
 * Dispatches the new operator files object to the store
 * @param updatedSyncedFiles Operator files object to update
 */
async function updateSyncedFilesDispatch(updatedSyncedFiles: SyncedFiles) {
  //get the updatedOperator Files id
  const id = updatedSyncedFiles?.id;

  // Acquire the mutex before proceeding
  await mutex.runExclusive(async () => {
    const { syncedFiles } = store.getState().files; // Get the latest state of the syncedFiles from the store

    // Get the latest state of the syncedFiles from the store
    const currentSyncedFiles = cloneDeep(syncedFiles);

    // Find the index of the operator file to update
    const operatorFilesUpdatedIndex = currentSyncedFiles.findIndex(files => files.id === id);

    // Update the existing operator files object
    if (operatorFilesUpdatedIndex >= 0) {
      currentSyncedFiles[operatorFilesUpdatedIndex] = updatedSyncedFiles;
    } else {
      currentSyncedFiles.push(updatedSyncedFiles);
    }

    // Dispatch the updated files to the store
    store.dispatch(setSyncedFiles(currentSyncedFiles));
  });
}

/**
 * Gets all the files of the project or fund or portfolio or offering from the server and updates the files in the redux store
 * @param token Auth token
 * @param client GraphQL client
 * @param id id related to the project or fund or portfolio or offering
 * @param syncedFiles All the files of the synced files from the redux store
 * @returns Updated files of the project or fund or portfolio or offering from server
 */
async function getFilesFromServer(
  token: string,
  client: GraphQLClient,
  id: string,
): Promise<DraftFile[]> {
  //update the sync status to pending
  updateSyncedFilesDispatch({
    id,
    files: [],
    syncWithServer: syncFilesWithServerEnum.PENDING,
  });

  //todo nir remove all old get calls
  //todo nir split this file?

  //get user role from redux
  const { user } = store.getState().user;
  const role = user?.role;

  //get the files from the server
  const newFilesServer =
    role === UserRolesEnum.operator || role === UserRolesEnum.admin
      ? (await getDraftFilesById(token ?? process.env.REACT_APP_TOKEN, client, [id])) ?? []
      : (await getPublicFilesByIds(token ?? process.env.REACT_APP_TOKEN, client, id)) ?? [];

  //extract the files from the server response
  const newFiles: DraftFile[] =
    role === UserRolesEnum.operator || role === UserRolesEnum.admin
      ? newFilesServer?.getDraftFilesByIds ?? []
      : newFilesServer?.getInvestorPublicFiles ?? [];

  //create a new operator files object
  const syncedFilesUpdated: SyncedFiles = {
    id: id,
    files: newFiles,
    syncWithServer: syncFilesWithServerEnum.SYNCED,
  };

  //update the operator files object in the store
  updateSyncedFilesDispatch(syncedFilesUpdated);

  return newFiles;
}
/**
 * Gets all the files of the project or fund or portfolio or offering from the server and updates the files in the redux store
 * @param isViewAs Whether the user is viewing as an investor
 * @param token Auth token
 * @param client Auth client
 * @param ids Investment ids for the entities
 * @returns Main images of the entities
 */
export const GetAllEntitiesMainFiles = async (
  isViewAs: boolean,
  token: string,
  client: any,
  ids: string[],
): Promise<DraftFile[] | PublicFile[]> => {
  // Use mutex to prevent multiple calls
  const release = await mutex.acquire();

  try {
    // Get the investments by entity
    let imagesFromServer: DraftFile[] = [];

    // Try to fetch from the store
    const syncedFiles = store.getState().files.syncedFiles;

    // Find the synced files id for all entities main images
    const syncedFilesMatch = syncedFiles?.find(synced => synced.id === ALL_ENTITIES_MAIN_IMAGES);

    // If the synced files are synced with the server - return the files
    if (syncedFilesMatch?.syncWithServer === syncFilesWithServerEnum.SYNCED) {
      return syncedFilesMatch.files;
    }

    // Else get the files from the server
    // Getting the draft files
    if (isViewAs) {
      // Note this won't save the files in the synced files store
      imagesFromServer = await getInvestorDraftProjectFundFiles(
        token,
        client,
        ids,
        FileLocationEnum.Main,
      );
    } else {
      // Load all the files of the investments once (without personal files)
      // Get the images from the server
      // Note this won't save the files in the synced files store
      imagesFromServer = await getInvestorPublicProjectFundFiles(
        token,
        client,
        ids,
        FileLocationEnum.Main,
      );
    }

    // Store the files in the redux store
    const updatedSyncedFiles = [
      ...syncedFiles,
      {
        id: ALL_ENTITIES_MAIN_IMAGES,
        files: imagesFromServer,
        syncWithServer: syncFilesWithServerEnum.SYNCED,
      },
    ];

    // Dispatch the updated files to the store
    dispatch(setSyncedFiles(updatedSyncedFiles));

    return imagesFromServer;
  } catch (error) {
    // Handle errors appropriately
    console.error('Failed to get entity main files', error);
    throw error;
  } finally {
    // Release the mutex lock
    release();
  }
};

/**
 * Matches the context to the file locations
 * @param context Context to match the files by
 * @param locations File locations
 * @returns Whether the file matches the context
 */
const matchContextToFileLocation = (context: FileTagEnum, locations: FileLocation[]): boolean => {
  //validate locations
  if (!locations?.length && context !== FileTagEnum.MediaCenter) return false;

  //Whether the file is in the media center
  const isInMediaCenter: boolean = locations?.some(
    (location: FileLocation) =>
      location?.locationInPage === FileLocationEnum.MediaCenter ||
      location?.locationInPage === FileLocationEnum.Gallery ||
      location?.locationInPage === FileLocationEnum.Documents ||
      location?.locationInPage === FileLocationEnum.Main ||
      location?.locationInPage === FileLocationEnum.Updates ||
      location?.locationInPage === FileLocationEnum.Logo ||
      location?.locationInPage === null, //include files with no specific location
  );

  //switch by context
  switch (context) {
    //handle investor first
    case FileTagEnum.Investor:
    case FileTagEnum.Viewer:
      return locations?.some(location => location?.entityType === FileEntityTypeEnum.Personal);

    case FileTagEnum.Main:
      return locations?.some(location => location?.locationInPage === FileLocationEnum.Main);

    case FileTagEnum.Update:
      return locations?.some(location => location?.locationInPage === FileLocationEnum.Updates);

    case FileTagEnum.Documents:
      return locations?.some(location => location?.locationInPage === FileLocationEnum.Documents);

    case FileTagEnum.Gallery:
      return locations?.some(location => location?.locationInPage === FileLocationEnum.Gallery);

    case FileTagEnum.MediaCenter:
      return isInMediaCenter;

    case FileTagEnum.Logo:
      return locations?.some(location => location?.locationInPage === FileLocationEnum.Logo);

    default:
      return false;
  }
};

/**
 * Gets all the files of the project or fund or portfolio or offering from the store or from the server
 * @param token The token of the user
 * @param client Client to send the request to
 * @param context Context to match the files by
 * @param id id of the project or fund or portfolio or offering
 * @returns Matching files of the project or fund or portfolio or offering
 */
export async function getFilesById(
  token: string,
  client: GraphQLClient,
  context: FileTagEnum,
  id: string,
) {
  //validate inputs
  if (!client || !token) return [];
  if (!context || !id) return [];

  //get the files related to the component by id
  let syncedFiles: SyncedFiles[] = store.getState().files.syncedFiles;
  let syncedFilesById: SyncedFiles = syncedFiles?.find(files => files.id === id);

  //if operatorFilesById syncWithServer is pending - wait for the files to be fetched from the server
  //First time entry will be with operatorFilesById undefined - not entering the loop and requesting the files from the server
  if (syncedFilesById?.syncWithServer === syncFilesWithServerEnum.PENDING) {
    //loop until the files are fetched from the server or until 4 seconds passed
    let maxTime = 0;
    while (
      (!syncedFilesById || syncedFilesById.syncWithServer === syncFilesWithServerEnum.PENDING) &&
      maxTime < 6000
    ) {
      //wait for 500ms
      await new Promise(resolve => setTimeout(resolve, 500));

      //update max time
      maxTime += 500;

      //get the files from the store
      syncedFiles = store.getState().files.syncedFiles;
      syncedFilesById = syncedFiles?.find(files => files.id === id);
    }
  }

  //get files from the store if there are no changes
  const files: DraftFile[] =
    syncedFilesById?.syncWithServer === syncFilesWithServerEnum.SYNCED
      ? syncedFilesById?.files
      : context === FileTagEnum.Investor
      ? await getDocumentsCenterPersonalFiles(token, client, [id])
      : await getFilesFromServer(token, client, id);

  //filter by the tags of the file
  const filteredByContext =
    files?.filter(file => matchContextToFileLocation(context, file?.locations)) ?? [];

  //filtering by the investor id
  const filterdByInvestorId =
    context === FileTagEnum.Investor
      ? filteredByContext?.filter(file => file?.tags?.some(tag => tag?.legalEntityId === id))
      : filteredByContext;

  return filterdByInvestorId;
}

/**
 * Gets all the files of the given entities and the personal files if includePersonalFiles is true
 * @param token Auth token
 * @param client GraphQL client
 * @param id Id of the entities to fetch (projects, operator and funds)
 * @returns All public files of the given entities and the personal files if includePersonalFiles is true
 */
export async function getPublicFilesByIds(
  token: string,
  client: GraphQLClient,
  id: string,
): Promise<DraftFile[]> {
  //validate input
  if (!id) return [];

  //fetch existing synced files from redux instead of calling the server
  const { syncedFiles } = store.getState().files;

  //loop over the synced files and find matched ids
  const foundFiles: SyncedFiles = syncedFiles?.find((synced: SyncedFiles) => {
    //if the synced files is synced and the id is in the ids - add the files to the matched files
    return synced?.syncWithServer === syncFilesWithServerEnum.SYNCED && id === synced?.id;
  });

  //if synced files found return the files
  if (foundFiles) return foundFiles?.files;

  //get public files from server
  const filesResponse = await getInvestorPublicFiles(token, client, [id], false);

  //update in redux
  updateSyncedFilesDispatch({
    id,
    files: filesResponse,
    syncWithServer: syncFilesWithServerEnum.SYNCED,
  });

  return filesResponse;
}

/**
 * Gets all the files of the given entities and the personal files if includePersonalFiles is true
 * @param token Auth token
 * @param client GraphQL client
 * @param ids Ids of the entities to fetch (projects, operator and funds)
 * @param locationType Location type of the files
 * @returns All public files of the given entities filtered by the given tag
 */
export async function getPublicProjectsFunds(
  token: string,
  client: GraphQLClient,
  ids: string[], //projects, funds, portfolios, offerings
  locationType?: FileLocationEnum,
): Promise<DraftFile[]> {
  //validate input
  if (!ids) return [];

  //fetch existing synced files from redux instead of calling the server
  const { syncedFiles } = store.getState().files;

  //loop over the synced files and find matched ids
  const foundFiles: SyncedFiles[] = syncedFiles?.filter((synced: SyncedFiles) => {
    //if the synced files is synced and the id is in the ids - add the files to the matched files
    return synced?.syncWithServer === syncFilesWithServerEnum.SYNCED && ids.includes(synced?.id);
  });

  //find the files that are not synced
  const notSyncedIds = ids.filter(id => !foundFiles.some(file => file.id === id)) ?? [];

  // if there are no files that are not synced - return the files
  if (!notSyncedIds?.length) return foundFiles.flatMap(file => file.files);

  //get public files from server
  const filesResponse = await getInvestorPublicProjectFundFiles(
    token,
    client,
    notSyncedIds,
    locationType,
  );

  return filesResponse;
}

/**
 * Gets all the files of the given entities and the personal files if includePersonalFiles is true
 * @param token Auth token
 * @param client GraphQL client
 * @param ids Ids of the entities to fetch (projects, operator and funds)
 * @param groupStringId Group string id of the files - used to synced a group of files
 * @returns All draft files of the given entities and the personal files if includePersonalFiles is true
 *
 */
export async function getAllDraftFiles(
  token: string,
  client: GraphQLClient,
  ids: string[],
  groupStringId?: string,
): Promise<DraftFile[]> {
  //fetch existing synced files from redux instead of calling the server
  const { syncedFiles } = store.getState().files;

  //look for personal files in redux
  const personalFiles: SyncedFiles = syncedFiles?.find(
    synced =>
      synced?.id === groupStringId && synced.syncWithServer === syncFilesWithServerEnum.SYNCED,
  );

  //if personal files found and include personal files is true - add the files to the matched files
  if (personalFiles) return personalFiles?.files;

  //get the files from the server
  const filesResponse = await getDraftFilesById(token, client, ids);
  const filesFromServer = filesResponse?.getDraftFilesByIds ?? [];

  //validate response
  if (!filesFromServer?.length || filesFromServer?.length === 0) return [];

  //update in redux
  updateSyncedFilesDispatch({
    id: groupStringId,
    files: filesFromServer,
    syncWithServer: syncFilesWithServerEnum.SYNCED,
  });

  return filesFromServer;
}

/**
 * Gets all the files of the given entities and the personal files if includePersonalFiles is true
 * @param token Auth token
 * @param client GraphQL client
 * @param ids Ids of the entities to fetch (projects, operator and funds)
 * @param groupStringId Group string id of the files - used to synced a group of files
 * @returns All public files of the given entities and the personal files if includePersonalFiles is true
 *
 */
export async function getAllPublicFiles(
  token: string,
  client: GraphQLClient,
  ids: string[],
  groupStringId?: string,
): Promise<DraftFile[]> {
  //fetch existing synced files from redux instead of calling the server
  const { syncedFiles } = store.getState().files;

  //look for personal files in redux
  const personalFiles: SyncedFiles = syncedFiles?.find(
    synced =>
      synced?.id === groupStringId && synced.syncWithServer === syncFilesWithServerEnum.SYNCED,
  );

  //if personal files found and include personal files is true - add the files to the matched files
  if (personalFiles) return personalFiles?.files;

  //get the files from the server
  const filesResponse = await getInvestorPublicFiles(token, client, ids, true);
  const filesFromServer = filesResponse?.getInvestorPublicFiles ?? [];

  //validate response
  if (!filesFromServer?.length || filesFromServer?.length === 0) return [];

  //update in redux
  updateSyncedFilesDispatch({
    id: groupStringId,
    files: filesFromServer,
    syncWithServer: syncFilesWithServerEnum.SYNCED,
  });

  return filesFromServer;
}

/**
 * Gets all personal files of the user from the server
 * @param token Auth token
 * @param client GraphQL client
 * @param legalEntityIds All the legal entity ids of the user
 * @returns All personal files of the user
 */
export async function getDocumentsCenterPersonalFiles(
  token: string,
  client: GraphQLClient,
  legalEntityIds: string[],
): Promise<DraftFile[]> {
  //validate input
  if (!legalEntityIds?.length || legalEntityIds.length === 0) return [];

  //fetch existing synced files from redux instead of calling the server
  const { syncedFiles } = store.getState().files;

  //get the combined legal entities ids for fetching all the legal entities files in one request from redux
  const combinedLegalEntityIds = legalEntityIds?.join(',');

  //loop over the synced files
  const foundSynced: SyncedFiles = syncedFiles?.find((singleSyncedFiles: SyncedFiles) => {
    //if the file is not in the legal entities ids or not synced - skip
    return (
      singleSyncedFiles.id === combinedLegalEntityIds &&
      (singleSyncedFiles.syncWithServer === syncFilesWithServerEnum.SYNCED ||
        singleSyncedFiles.syncWithServer === syncFilesWithServerEnum.DOCUMENT_CENTER_ONLY)
    );
  });

  //if synced files found return the files
  if (foundSynced) return foundSynced.files;

  const filesResponse = await getInvestorDraftFilesById(token, client, legalEntityIds);
  const filesFromServer = filesResponse?.getInvestorDraftFiles ?? [];

  //validate response
  if (!filesFromServer?.length || filesFromServer?.length === 0) return [];

  //update in redux
  updateSyncedFilesDispatch({
    id: combinedLegalEntityIds,
    files: filesFromServer,
    syncWithServer: syncFilesWithServerEnum.SYNCED,
  });

  return filesFromServer;
}

export async function getFilesForDocumentsCenter(
  token,
  role: Role,
  context,
  data,
  isTags?,
  client?: GraphQLClient,
): Promise<DraftFile[]> {
  //todo nir - refactor this with file service

  const legalEntityIds: string[] = data?.investor?.userLegalEntities?.map(
    legalEntity => legalEntity?.id,
  );

  if (!legalEntityIds?.length) return [];

  // const files = await getInvestorDraftFilesById(token, client, legalEntityIds);

  return [];
}

//todo remove existing delete message since now it is pending and not published
/**
 * Sets the new files to the operator files object in the store
 * @param filesToDelete Files to delete
 */
export const updateSyncedFilesAfterDelete = (filesToDelete: DraftFile[]) => {
  //validate input
  if (!filesToDelete?.length) return;

  //get the files ids
  const filesIds = filesToDelete?.map(file => file?.id);

  //filter the files ids out of the operator files
  const relatedSyncedFiles: SyncedFiles[] = store
    .getState()
    .files.syncedFiles?.map((singleSyncedFiles: SyncedFiles) => {
      //filtered files
      const filteredFiles =
        singleSyncedFiles?.files?.filter(file => !filesIds?.includes(file?.id)) ?? [];
      return { ...singleSyncedFiles, files: filteredFiles };
    });

  //update the files in the store
  store.dispatch(setSyncedFiles(relatedSyncedFiles));
};

/**
 * Sets the new files to the operator files object in the store
 * @param newFiles New files to set
 */
export const updateOperatorFilesAfterUpload = (newFiles: DraftFile[]) => {
  //validate input
  if (!newFiles?.length) return;

  //get the related entity ids
  const relatedEntityIds = newFiles?.flatMap(file =>
    file?.locations?.map(location => location?.entityId),
  );

  //make the ids unique
  const uniqueIds = [...new Set(relatedEntityIds)];

  //check if at least one of the ids is in the operator files
  let foundMatch: boolean = false;

  //find related OperatorFiles objects and set syncWithServer to false
  const relatedSyncedFiles: SyncedFiles[] = store
    .getState()
    .files.syncedFiles?.map((singleSyncedFiles: SyncedFiles) => {
      const syncedFilesId: string = singleSyncedFiles?.id;
      const matchedId: boolean = uniqueIds?.includes(syncedFilesId);

      //if there is no matched id
      if (!matchedId) return singleSyncedFiles;

      //set found match to true
      foundMatch = true;

      //update the files in the operator files
      const updatedFiles: DraftFile[] = singleSyncedFiles?.files?.map(file => {
        //get the file id
        const fileId = file?.id;

        //find the file in the operator files
        const matchedFile = newFiles?.find(existingFile => existingFile?.id === fileId);

        //if the file was found update it with the new file
        return matchedFile ? matchedFile : file;
      });

      //get just the new files to be added
      const newFilesToAdd: DraftFile[] = newFiles?.filter(
        newFile => !updatedFiles?.some(updatedFile => updatedFile?.id === newFile?.id),
      );

      //combine the updated files with the new files
      const combinedFiles: DraftFile[] = [...updatedFiles, ...newFilesToAdd];

      //add the new files to the operator files
      return {
        // ...singleSyncedFiles,
        id: syncedFilesId,
        syncWithServer: syncFilesWithServerEnum.SYNCED,
        files: combinedFiles,
      };
    });

  //if there was no match to be found - add the files in a new operator files object
  if (!foundMatch && uniqueIds?.length > 0) {
    const newSyncedFiles: SyncedFiles = {
      id: uniqueIds[0],
      files: newFiles,
      syncWithServer: syncFilesWithServerEnum.SYNCED,
    };

    //add the new operator files to the related operator files
    relatedSyncedFiles.push(newSyncedFiles);
  }

  //update the files in the store
  store.dispatch(setSyncedFiles(relatedSyncedFiles));
};

/**
 * Uploads new files to the server
 * @param token Auth token
 * @param files Files to upload
 * @param knownTagsArray Files known tags
 * @param locations Files locations in platform
 * @param tagByContent Whether to tag the files by their content
 * @returns Records of the uploaded files
 */
export async function uploadNewFilesApi(
  token: string,
  files: File[],
  knownTagsArray: Array<FileTags[]>,
  locations: Array<FileLocation[]>,
  tagByContent: boolean = false,
  // disableErrorHandling = false,
): Promise<DraftFile[]> {
  //token to use for debugging
  const tokenToUse = process.env.REACT_APP_TOKEN ?? token;

  //validate input
  if (!files || files?.length === 0) return [];

  //setting the total number of files to upload in the loading modal
  dispatch(setLoadingUploadProgressBar(true));
  dispatch(setTotalToLoad(files.length));

  //append all files to the form data
  const formData = new FormData();
  files &&
    files.forEach(file => {
      formData.append('files', file);
    });

  //location from smart documents
  const personalFiles: boolean = locations?.some(singleLocations =>
    singleLocations?.some(location => location?.entityType === FileEntityTypeEnum.Personal),
  );

  // known tag array is null or empty
  const knownTagsArrayAreNullOrEmpty: boolean =
    !knownTagsArray || knownTagsArray?.filter(valid => valid)?.length === 0;

  //Personal files and known tags array is null or empty
  const shouldAutoTag: boolean = personalFiles && knownTagsArrayAreNullOrEmpty;

  //set the url for the request
  const url = `${process.env.REACT_APP_API_URL}uploadDraftFiles`; // your GraphQL server URL

  //append the known tags array
  if (knownTagsArray?.length) formData.append('knownTagsArray', JSON.stringify(knownTagsArray));

  //append the locations array
  if (locations?.length) formData.append('locations', JSON.stringify(locations));

  //append the tag by content flag
  formData.append('tagByContent', JSON.stringify(tagByContent));

  //append the auto tag flag
  formData.append('shouldAutoTag', JSON.stringify(shouldAutoTag));

  //send the request
  const res = await fetch(url, {
    method: 'post',
    body: formData,
    headers: {
      Authorization: `Bearer ${tokenToUse}`,
      // 'Content-Type': 'application/json',
      // 'Content-Type': 'multipart/form-data; boundary=----WebKitFormBoundary7MA4YWxkTrZu0gW',
      Role: UserRolesEnum.operator,
    },
  });

  //get the response
  const response: DraftFile[] = await res.json();

  //Close the loading modal
  dispatch(setLoadingUploadProgressBar(false));
  dispatch(setTotalToLoad(0));

  // Update the operator files in the store with the new files
  if (response?.length > 0) updateOperatorFilesAfterUpload(response);

  //return the response
  return response;
}

/**
 * Adds the given files to the store
 * @param state Current state of the store
 * @param files Given files to be added to the store
 */
export const addInvestorsFilesToStoreService = (state, files) => {
  //validate files input
  if (!files) return;

  //remove duplicates
  const uniqueFiles =
    files?.filter((file: DraftFile, index: number, self: DraftFile[]) => {
      return (
        index === self.findIndex((t: DraftFile) => t.id === file.id && t.fileName === file.fileName)
      );
    }) ?? [];

  //find the new files that exists in files but not in allFiles
  const newFiles = uniqueFiles.filter(
    file => !state.allFiles.some(allFile => allFile.id === file.id),
  );
  //update the state
  state.allFiles = uniqueFiles;
  state.newFiles = newFiles;

  state.filesForView = applyFilters(
    uniqueFiles,
    state.activeFilters,
    state.filesForView,
    false,
    state,
  );
};

export const removeUnselectedTabFilter = (state, tabs) => {
  let selectedTabFilter = state.activeFilters.find(
    _filter => _filter.key === tabs[state.selectedTab].type,
  );
  if (selectedTabFilter) {
    let isFilterInTaggedFiles = state.taggedFilesArray.some(
      tag => tag.key === selectedTabFilter.value,
    );
    if (!isFilterInTaggedFiles) {
      state.activeFilters = state.activeFilters.filter(
        _filter => _filter.key !== tabs[state.selectedTab].type,
      );
    }
  }
};

const updateCounters = (state, file) => {
  if (file?.tags?.some(tag => [FileTagEnum.Investor].includes(tag.type))) {
    state.investorsFilesCounter = state.investorsFilesCounter + 1;
  } else {
    state.untaggedCounter = state.untaggedCounter + 1;
  }
};

export const applyFilters = (files, activeFilters, filesForView, isFileFromApi = false, state) => {
  let res;

  //init counters
  state.investorsFilesCounter = 0;
  state.untaggedCounter = 0;
  let filteredFiles = files.filter(file => {
    updateCounters(state, file);

    return activeFilters.every(filter => {
      switch (filter.key) {
        case FileTagEnum.Investor:
          return file?.tags.some(
            tag =>
              tag?.type === filter.key && tag?.extra?.toLowerCase() === filter.value?.toLowerCase(),
          );
        case 'search':
          return filter.value
            .split(' ')
            .filter(value => !!value)
            .every(
              value =>
                file?.description?.toLowerCase().includes(value.toLowerCase()) || // Search in description
                file?.fileName?.toLowerCase().includes(value.toLowerCase()) || // Search in description
                file?.tags?.some(tag => tag?.value?.toLowerCase().includes(value.toLowerCase())), // Search in tags
            );
        case 'selected_tab':
          if (filter.value === FileTagsValues.UNTAGGED) {
            return !file?.tags?.some(tag => [FileTagEnum.Investor].includes(tag.type));
          } else {
            return file?.tags?.some(tag => tag.type === filter.value);
          }
      }
    });
  });
  res = isFileFromApi ? [...filesForView, ...filteredFiles] : filteredFiles;
  return res;
};

export const handleSearchFiltersChange = (activeFilters, searchString) => {
  let tempActiveFilters = cloneDeep(activeFilters);

  let filterIdx = activeFilters.findIndex(filter => filter.key === 'search');
  if (filterIdx === -1) {
    tempActiveFilters.push({ key: 'search', value: searchString });
  } else if (searchString === '') {
    tempActiveFilters.splice(filterIdx, 1);
  } else {
    tempActiveFilters[filterIdx].value = searchString;
  }
  return tempActiveFilters;
};

export const handleTagsFiltersChange = (activeFilters, tagData) => {
  //validate input
  if (!tagData || Object.keys(tagData).length === 0) return activeFilters;

  const tagFilter = { key: tagData.value.type, value: tagData.key };
  let tempActiveFilters = cloneDeep(activeFilters);

  let filterIdx = activeFilters.findIndex(
    filter => tagFilter.key === filter.key && tagFilter.value === filter.value,
  );

  let filterTypeIdx = activeFilters.findIndex(filter => tagFilter.key === filter.key);

  if (filterIdx > -1) {
    // When the same tagFilter pressed, turn it off
    tempActiveFilters.splice(filterIdx, 1);
  } else if (filterTypeIdx > -1) {
    // switch between 2 tag filters
    tempActiveFilters.splice(filterTypeIdx, 1);
    tempActiveFilters.push(tagFilter);
  } else {
    // first selection of tag filter
    tempActiveFilters.push(tagFilter);
  }
  return tempActiveFilters;
};

export const handleTabsFilterChange = (activeFilters, selectedTab) => {
  let tempActiveFilters = cloneDeep(activeFilters);

  let filterIdx = activeFilters.findIndex(filter => filter.key === 'selected_tab');
  if (filterIdx === -1) {
    tempActiveFilters.push({ key: 'selected_tab', value: selectedTab });
  } else {
    tempActiveFilters[filterIdx].value = selectedTab;
  }

  const globalFiltersOnly = tempActiveFilters.filter(filter =>
    ['selected_tab', 'search'].includes(filter.key),
  );

  return globalFiltersOnly;
};

export const updateTabData = (selectedType, files, taggedFilesArray, isTabChanged = false) => {
  const taggedFilesList = files?.reduce((acc, file) => {
    if (file?.tags)
      file.tags.forEach(tag => {
        if (tag.type === selectedType) {
          let tagExtraAsLC = tag.extra?.toLowerCase();

          //exclude viewer tag
          if (tag.value === FileTagEnum.Viewer) return acc;

          //find the new counter
          const counter: number = acc[tagExtraAsLC] ? acc[tagExtraAsLC].counter + 1 : 1;

          //add to the tagged files
          acc = { ...acc, [tagExtraAsLC]: { type: tag.type, value: tag.value, counter } };
        }
      });
    return acc;
  }, {});

  return prepareTagFilesListForUI(taggedFilesList, isTabChanged ? [] : taggedFilesArray);
};

const prepareTagFilesListForUI = (newTagFiles, tagFiles) => {
  Object.keys(newTagFiles).forEach(tag => {
    let foundTag = tagFiles.find(_tag => _tag.key === tag);
    if (!foundTag || !foundTag.value) {
      tagFiles.push({
        key: tag,
        value: newTagFiles[tag],
      });
    }
  });

  return tagFiles;
};

/**
 * Deletes the input for the updateFilesLocations function
 */
export type UpdateFileLocationsFunctionInput = {
  file: DraftFile;

  newLocations: FileLocation[];
};

/**
 * Deletes the input for the updateFilesLocations function
 */
export type UpdateFileTagsFunctionInput = {
  file: DraftFile;
  newTags: FileTags[];
};

/**
 * Sorts the files by their index in the page
 * @param files Given files to be sorted
 * @param locationInPage Location in the page to sort by
 * @param innerIdComponent Weather to filter by fund asset id
 * @returns Sorted files
 */
export const sortFilesByIndex = (
  files: DraftFile[],
  locationInPage: FileLocationEnum = FileLocationEnum.Gallery,
  innerIdComponent?: string,
): DraftFile[] => {
  if (!files || files.length === 0) return [];

  return (
    [...files]?.sort((a, b) => {
      const aLocationWithIndex = a.locations?.find(
        loc =>
          loc.sortIndex !== undefined &&
          loc.locationInPage === locationInPage &&
          (!innerIdComponent || loc.locationInnerId === innerIdComponent),
      );
      const bLocationWithIndex = b.locations?.find(
        loc =>
          loc.sortIndex !== undefined &&
          loc.locationInPage === locationInPage &&
          (!innerIdComponent || loc.locationInnerId === innerIdComponent),
      );

      const aSortIndex = aLocationWithIndex?.sortIndex ?? Infinity;
      const bSortIndex = bLocationWithIndex?.sortIndex ?? Infinity;

      return aSortIndex - bSortIndex;
    }) ?? []
  );
};
